import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

// Types
export interface Wall {
  id: string;
  floor_plan_id: string;
  points: { x: number; y: number }[];
  length_pixels: number;
  confidence: number;
  calibration_ready: boolean; // AI-marked as suitable for calibration (pixel-perfect, grid-aligned)
  used_for_calibration: boolean; // User-selected for calibration
  calibration_real_length?: number;
  calibration_units?: 'ft' | 'm' | 'cm' | 'in';
  created_at: string;
  updated_at: string;
}

export interface UpdateWallParams {
  used_for_calibration?: boolean;
  calibration_real_length?: number;
  calibration_units?: 'ft' | 'm' | 'cm' | 'in';
}

/**
 * Get all walls for a floor plan
 */
export const getWalls = async (floorPlanId: string): Promise<Wall[]> => {
  const client = await getAuthenticatedSupabaseClient();
  const { data, error } = await client.from('walls').select('*').eq('floor_plan_id', floorPlanId);

  if (error) {
    logger.error('Error fetching walls:', error);
    throw error;
  }

  return data || [];
};

/**
 * Get a single wall by ID
 */
export const getWall = async (id: string): Promise<Wall | null> => {
  const client = await getAuthenticatedSupabaseClient();
  const { data, error } = await client.from('walls').select('*').eq('id', id).single();

  if (error) {
    logger.error('Error fetching wall:', error);
    throw error;
  }

  return data;
};

/**
 * Update a wall (primarily for calibration data)
 */
export const updateWall = async (id: string, updates: UpdateWallParams): Promise<Wall> => {
  const client = await getAuthenticatedSupabaseClient();
  const { data, error } = await client.from('walls').update(updates).eq('id', id).select().single();

  if (error) {
    logger.error('Error updating wall:', error);
    throw error;
  }

  return data;
};

/**
 * Mark a wall as used for calibration
 * Also resets other walls' calibration flag for this floor plan
 */
export const setWallForCalibration = async (
  wallId: string,
  floorPlanId: string,
  realLength: number,
  units: 'ft' | 'm' | 'cm' | 'in',
): Promise<void> => {
  const client = await getAuthenticatedSupabaseClient();

  // Reset all other walls for this floor plan
  const { error: resetError } = await client
    .from('walls')
    .update({
      used_for_calibration: false,
      calibration_real_length: null,
      calibration_units: null,
    })
    .eq('floor_plan_id', floorPlanId);

  if (resetError) {
    logger.error('Error resetting wall calibration:', resetError);
    throw resetError;
  }

  // Set this wall as calibration wall
  const { error: updateError } = await client
    .from('walls')
    .update({
      used_for_calibration: true,
      calibration_real_length: realLength,
      calibration_units: units,
    })
    .eq('id', wallId);

  if (updateError) {
    logger.error('Error setting wall for calibration:', updateError);
    throw updateError;
  }
};

/**
 * Get the wall used for calibration (if any)
 */
export const getCalibrationWall = async (floorPlanId: string): Promise<Wall | null> => {
  const client = await getAuthenticatedSupabaseClient();
  const { data, error } = await client
    .from('walls')
    .select('*')
    .eq('floor_plan_id', floorPlanId)
    .eq('used_for_calibration', true)
    .maybeSingle();

  if (error) {
    logger.error('Error fetching calibration wall:', error);
    throw error;
  }

  return data;
};

/**
 * Delete a wall
 */
export const deleteWall = async (id: string): Promise<void> => {
  const client = await getAuthenticatedSupabaseClient();
  const { error } = await client.from('walls').delete().eq('id', id);

  if (error) {
    logger.error('Error deleting wall:', error);
    throw error;
  }
};

/**
 * Delete all walls for a floor plan
 */
export const deleteAllWalls = async (floorPlanId: string): Promise<void> => {
  const client = await getAuthenticatedSupabaseClient();
  const { error } = await client.from('walls').delete().eq('floor_plan_id', floorPlanId);

  if (error) {
    logger.error('Error deleting walls:', error);
    throw error;
  }
};
